interface RowWithId {
    id: number;
}

/** Splits the snapshot's ids into batches of `pageSize`, in order. */
export function chunkSnapshotIds({
    ids,
    pageSize,
}: {
    ids: number[];
    pageSize: number;
}): number[][] {
    const batches: number[][] = [];
    for (let start = 0; start < ids.length; start += pageSize) {
        batches.push(ids.slice(start, start + pageSize));
    }
    return batches;
}

/**
 * Puts a batch's rows back in snapshot order. An id with no row (deleted
 * since the snapshot was taken) is left out.
 */
export function orderRowsByIds<TRow extends RowWithId>({
    ids,
    rows,
}: {
    ids: number[];
    rows: TRow[];
}): TRow[] {
    const rowsById = new Map(rows.map((row) => [row.id, row]));
    return ids.flatMap((id) => rowsById.get(id) ?? []);
}

export function getSnapshotHasMore({
    idCount,
    loadedBatchCount,
    pageSize,
}: {
    idCount: number;
    loadedBatchCount: number;
    pageSize: number;
}): boolean {
    return loadedBatchCount * pageSize < idCount;
}

/**
 * The snapshot's first rows followed by each loaded batch in snapshot order,
 * stopping at the first batch still loading so rows never jump ahead of a
 * gap. A row shown once is not shown again.
 */
export function mergeSnapshotRows<TRow extends RowWithId>({
    firstRows,
    batches,
}: {
    firstRows: TRow[];
    batches: Array<{ ids: number[]; rows: TRow[] | undefined }>;
}): TRow[] {
    const firstMissingIndex = batches.findIndex(
        ({ rows }) => rows === undefined,
    );
    const loadedBatches =
        firstMissingIndex === -1
            ? batches
            : batches.slice(0, firstMissingIndex);
    const seenIds = new Set<number>();
    return [
        firstRows,
        ...loadedBatches.map(({ ids, rows }) =>
            orderRowsByIds({ ids, rows: rows ?? [] }),
        ),
    ]
        .flat()
        .filter((row) => {
            if (seenIds.has(row.id)) {
                return false;
            }
            seenIds.add(row.id);
            return true;
        });
}
